import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useTracks } from "@/context/TrackContext";
import { LogOut, LayoutDashboard, Library as LibraryIcon, MessageSquare, Settings, KeyRound, Mic, NotebookPen, CircleDollarSign, Route } from "lucide-react";
import { ChangePasswordModal, ForcedPasswordGate } from "@/components/ChangePassword";
import { SupportBot } from "@/components/SupportBot";

export default function Shell() {
  const { user, logout } = useAuth();
  const { brand } = useWorkspace();
  const { tracks, activeId, setActive } = useTracks();
  const nav = useNavigate();
  const loc = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const item = (to, label, Icon) => {
    const active = loc.pathname === to || (to !== "/dashboard" && loc.pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
          active ? "text-[var(--brand-primary)]" : "text-neutral-600 hover:text-black"
        }`}
      >
        <Icon size={16} strokeWidth={1.5} />
        <span>{label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[color:var(--brand-bg)]">
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b hairline" style={{backgroundColor:'color-mix(in srgb, var(--brand-bg) 85%, transparent)'}}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-logo">
            <div className="w-8 h-8 flex items-center justify-center" style={{border:'1px solid var(--brand-primary)'}}>
              <span className="font-display font-semibold text-xs tracking-tight">{brand.logo_text}</span>
            </div>
            <div>
              <div className="font-display text-base leading-tight">{brand.name}</div>
              <div className="label-eyebrow leading-tight" style={{fontSize:'0.55rem'}}>{brand.tagline || "Onboarding Portal"}</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {item("/dashboard", "Dashboard", LayoutDashboard)}
            {item("/library", "Library", LibraryIcon)}
            {item("/scenarios", "Role-Plays", MessageSquare)}
            {item("/call-review", "Call Review", Mic)}
            {item("/notes", "My Notes", NotebookPen)}
            {item("/commissions", "Commissions", CircleDollarSign)}
            {isAdmin && item("/admin", "Admin", Settings)}
          </nav>

          <div className="flex items-center gap-4">
            {tracks.length > 1 && (
              <div className="hidden sm:flex items-center gap-2 border hairline bg-white px-2 py-1.5" data-testid="track-switcher">
                <Route size={14} strokeWidth={1.5} className="text-[var(--brand-primary)]" />
                <select
                  value={activeId}
                  onChange={(e) => setActive(e.target.value)}
                  className="bg-transparent text-xs focus:outline-none max-w-[160px]"
                  data-testid="track-switcher-select"
                  title="Training track"
                >
                  <option value="">All tracks</option>
                  {tracks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <div className="hidden sm:block text-right">
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="label-eyebrow" style={{fontSize:'0.55rem'}}>{user?.role}</div>
            </div>
            <button
              onClick={() => setPwOpen(true)}
              className="btn-ghost text-sm flex items-center gap-2"
              data-testid="change-password-btn"
              title="Change password"
            >
              <KeyRound size={14} strokeWidth={1.5} /><span className="hidden sm:inline">Password</span>
            </button>
            <button
              onClick={() => { logout(); nav("/"); }}
              className="btn-ghost text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut size={14} strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14" key={activeId}>
        <ForcedPasswordGate>
          <Outlet />
        </ForcedPasswordGate>
      </main>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      <SupportBot />
    </div>
  );
}
